import { useLazyQuery } from '@apollo/react-hooks';
import {
	Box,
	createStyles,
	Grid,
	makeStyles,
	Paper,
	useMediaQuery
} from '@material-ui/core';
import Bowser from 'bowser';
import gql from 'graphql-tag';
import {
	ArrowsChevronSmallLeft,
	ExtendedTheme,
	Link,
	Loader,
	MessageView,
	TextBlock,
	useTheme
} from 'neon-design-system';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
	CHANNEL_FIELDS_FRAGMENT,
	GET_PREMIUM_CHANNEL_BY_ID
} from '../../../gql/channels';
import {
	Article,
	ChannelTypes,
	PromotionalContentSummary,
	User
} from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { getUsersWithNames } from '../../../utils/filtering.utils';
import { getLinkPrefix } from '../../../utils/list-utils';
import { promotionalContentShortcodesToHtml } from '../../../utils/promotional-content.utils';
import { useArticleActivityLogger } from '../../hooks/use-activity-logger';
import { useViewStats } from '../../hooks/use-view-stats';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { useUser } from '../../providers/UserProvider';
import ArticleContent from '../../ui/ArticleContent';
import ArticleContributorList from '../../ui/ArticleContributorList';
import ArticleHeader from '../../ui/ArticleHeader';
import { ContributorProps } from '../../ui/Contributor';
import DeskEmail from '../../ui/DeskEmail';
import Disclaimer from '../../ui/Disclaimer';
import PromotionalContentSidebar from '../../ui/PromotionalContentSidebar';
import { getPublishedData } from '../../ui/PublishedBy';
import { ChannelCoverFetchData } from './ChannelCoverFetchData';
import { ChannelSubscriptionButton } from './ChannelSubscriptionButton';
import InterestedInPremium from './InterestedInPremium';
import { isChartsArticle } from '../../../utils/tableau';
import { getChannelLink } from '../../../utils/link.utils';
import ChannelCard from './ChannelCardNew';
import LatestArticlesFromChannel from './LatestArticlesFromChannel';
import ObjectID from 'bson-objectid';

const useStyles = makeStyles<ExtendedTheme>(theme =>
	createStyles({
		root: {
			[theme.breakpoints.only('md')]: {
				marginTop: theme.spacing(1)
			},
			'@media print': {
				maxWidth: '95%'
			}
		},
		noArticles: {
			padding: theme.spacing(8, 0, 4)
		},
		articleSidebar: {
			height: '100%',
			'@media print': {
				display: 'none'
			}
		},
		articleInfo: {
			'& > div:not(:last-child)': {
				marginBottom: theme.spacing(6)
			}
		},
		disclaimer: {
			marginTop: theme.spacing(8),
			'@media print': {
				marginTop: theme.spacing(4)
			}
		},
		marexPrintLogo: {
			display: 'none',
			width: 100,
			marginLeft: 8,
			'@media print': {
				display: 'block'
			}
		},
		'@global': {
			'@media print and (color)': {
				'*': {
					printColorAdjust: 'exact'
				}
			},
			'@media print': {
				body: {
					backgroundImage:
						'url(/insights/images/marex-dark-logo-vertical.png)',
					backgroundSize: 40,
					backgroundRepeat: 'no-repeat',
					backgroundPosition: 'top right'
				}
			}
		},
		backLink: {
			display: 'flex',
			alignItems: 'center',
			margin: theme.spacing(0, 0, 2, 1)
		},
		dividerWrapper: {
			[theme.breakpoints.up('md')]: {
				paddingLeft: theme.spacing(2),
				paddingRight: theme.spacing(2)
			},
			[theme.breakpoints.up('lg')]: {
				paddingLeft: theme.spacing(6),
				paddingRight: theme.spacing(6)
			}
		},
		verticalDivider: {
			[theme.breakpoints.up('md')]: {
				width: 1,
				height: '100%',
				background: theme.themeColors.grey[600]
			}
		}
	})
);

const FIND_ARTICLE_BY_ID = gql`
	query findPublishedArticleById($id: ID!) {
		article: findPublishedArticleById(id: $id) {
			_id
			title
			html
			publishedBy { _id name type }
			contributors { _id name position phone }
			categories { _id name }
			tags { _id name }
			summary
			publishDate
			lastEdited
			state
			version
			isPremium
			isPremiumActive
			premiumExpiryOffset
			premiumChannel
			premiumChannelDetails {
				${CHANNEL_FIELDS_FRAGMENT}
				userChannelSubscription {
					subscriptionState
				}
			}
			displayInChartSection
			canSeeFullVersion
			randomisedPromotionalContent {
				_id
				name
				link
				sidebarImage
			}
			deskEmail
			isLocked
			showWatermark
			isPinned
		}
		promotionalContent {
			_id
			name
			link
			articleImage
			expiryDate
		}
	}
`;

const ARTICLE_NOT_FOUND_ERROR = 'ARTICLE_NOT_FOUND';

const ArticlePage: React.FC = () => {
	const { _id } = useParams<{ _id: string }>();
	const isValidArticleId = ObjectID.isValid(_id);
	const classes = useStyles();
	const { theme } = useTheme();
	const showFullWidthArticleContent = useMediaQuery(
		theme.breakpoints.down('sm')
	);
	const { push } = useHistory();
	const addViewStats = useViewStats();
	const { userDetails } = useUser();
	const { printLog } = useLogger();

	const [isFixedLogoSupport, setIsFixedLogoSupport] = useState(false);
	const [article, setArticle] = useState<Article | undefined>();

	const [getChannel, { data: channelData }] = useLazyQuery(
		GET_PREMIUM_CHANNEL_BY_ID,
		{
			variables: { id: _id },
			fetchPolicy: 'network-only'
		}
	);

	const [findArticleByIdQuery, { loading, data, error }] = useLazyQuery<{
		article: Article;
		promotionalContent: PromotionalContentSummary[];
	}>(FIND_ARTICLE_BY_ID, {
		variables: { id: _id },
		fetchPolicy: 'network-only'
	});

	useEffect(() => {
		let article!: Article;

		if (data?.article) {
			article = data.article;
			window.Appcues?.track('Article View', {
				desk: article.publishedBy.name,
				articleIsPremium: article.isPremiumActive,
				userCanViewPremium: article.canSeeFullVersion
			});

			if (article.canSeeFullVersion) {
				addViewStats(article);
			}

			// all articles can be without Channel
			article.premiumChannel &&
				getChannel({
					variables: {
						id: article.premiumChannel
					}
				});
		}

		if (data?.article && data.promotionalContent.length) {
			article = {
				...data.article,
				html: promotionalContentShortcodesToHtml(
					data.article.html,
					data.promotionalContent
				)
			};
		}

		setArticle(article);
	}, [data]);

	useEffect(() => {
		if (isValidArticleId) {
			findArticleByIdQuery();
		}
	}, []);

	useEffect(() => {
		if (error && error.message.includes(ARTICLE_NOT_FOUND_ERROR)) {
			push(baseUrls.forYou);
		}
	}, [error]);

	const hideDisclaimer = article?.premiumChannel
		? channelData?.channel?.hideDisclaimer
		: false;

	useArticleActivityLogger(
		'Article',
		article?.title,
		article?._id,
		data?.article?.premiumChannelDetails?.name,
		data?.article?.premiumChannelDetails?._id
	);

	useEffect(() => {
		if (article) {
			if (!article?.canSeeFullVersion && article?.isPremiumActive) {
				printLog(
					LogLevel.INFO,
					LogAction.ViewPremiumWithoutAccess,
					'User attempted to view, premium article',
					{
						id: _id,
						title: article?.title
					}
				);
			}
			if (!article?.canSeeFullVersion && !article?.isPremiumActive) {
				printLog(
					LogLevel.INFO,
					LogAction.ViewLockedWithoutAccess,
					'User attempted to view article from locked category',
					{
						id: _id,
						title: article?.title
					}
				);
			}
		}
	}, [_id, article, printLog]);

	useEffect(() => {
		const printLogger = () => {
			printLog(
				LogLevel.INFO,
				LogAction.ArticlePrinted,
				'User printed article',
				{
					id: _id,
					title: article?.title
				}
			);
		};

		window.addEventListener('afterprint', printLogger);

		return () => {
			window.removeEventListener('afterprint', printLogger);
		};
	}, [_id, article, printLog]);

	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);

		if (browser.platform.type !== 'desktop') {
			setIsFixedLogoSupport(false);
		} else if (browser.browser.name !== 'Safari') {
			setIsFixedLogoSupport(true);
			document.body.style.backgroundImage = 'none';
		}
	}, []);

	const errorMessage = (
		<MessageView
			styleName="primary"
			size="large"
			title="Article not found"
			primaryAction={{
				label: 'Return to Neon Insights',
				onClick: () => push('/')
			}}
		></MessageView>
	);

	if (!isValidArticleId) return errorMessage;

	if (loading || !article || (article.premiumChannel && !channelData))
		return <Loader />;

	const chartsArticle =
		isChartsArticle(article.html) || article.displayInChartSection;

	const publishedData = getPublishedData(
		article,
		getLinkPrefix(),
		false,
		article.version > 1
	);

	const isClosedFreeChannel =
		article.premiumChannelDetails?.type === ChannelTypes.freeClosed;

	const verticalDivider = (
		<Box className={classes.dividerWrapper}>
			<Box className={classes.verticalDivider} />
		</Box>
	);

	const fullWidthContent = chartsArticle || showFullWidthArticleContent;

	return (
		<Grid container spacing={2} className={classes.root}>
			{isFixedLogoSupport && (
				<img
					className={classes.marexPrintLogo}
					alt="Marex"
					src="/insights/images/marex-dark-logo.png"
				/>
			)}
			{article.premiumChannelDetails ? (
				article.canSeeFullVersion ? (
					chartsArticle ? (
						<Link
							to={getChannelLink(
								article.premiumChannelDetails._id
							)}
							className={classes.backLink}
						>
							<ArrowsChevronSmallLeft />
							<strong>
								{article.premiumChannelDetails.name}
							</strong>
						</Link>
					) : (
						<></>
					)
				) : (
					<Grid item xs={12}>
						<Box mb={4}>
							<ChannelCoverFetchData
								channelId={article.premiumChannel}
							/>
						</Box>
					</Grid>
				)
			) : (
				<></>
			)}
			<Grid item xs={12}>
				<ArticleHeader
					title={article.title}
					summary={article.summary}
					id={article._id}
					publishedData={publishedData}
					channel={article.premiumChannelDetails}
					chartsArticle={chartsArticle}
				/>
			</Grid>
			<Grid item xs={12}>
				<Box
					display="flex"
					flexDirection={fullWidthContent ? 'column' : 'row'}
				>
					<Box style={{ width: fullWidthContent ? '100%' : '66%' }}>
						{article.canSeeFullVersion && article.html ? (
							<ArticleContent
								title={article.title}
								html={article.html}
								id={article._id}
								showWatermark={article.showWatermark}
								watermarkText={userDetails?.email}
							/>
						) : article.premiumChannelDetails ? (
							<Paper className={classes.noArticles}>
								<MessageView
									styleName="primary"
									size="medium"
									title={
										isClosedFreeChannel
											? `Become a subscriber of ${article.premiumChannelDetails.name} to access this article`
											: `Read more from ${article.premiumChannelDetails.name}`
									}
									icon={
										isClosedFreeChannel ? undefined : <></>
									}
								>
									<Box pb={4}>
										<TextBlock styleName="body" ink="muted">
											{isClosedFreeChannel ? (
												<>
													Once sent, your request will
													be reviewed by the channel
													owners, and you will be
													notified via email when your
													access is granted.
												</>
											) : (
												<>
													You’ve read all your free
													premium channel articles.
													Subscribe to the channel to
													get unlimited access.
												</>
											)}
										</TextBlock>
									</Box>
									<ChannelSubscriptionButton
										channel={article.premiumChannelDetails}
										colorType="primary"
										disclaimer={false}
									/>
								</MessageView>
							</Paper>
						) : (
							<InterestedInPremium
								articleTitle={article.title}
								deskEmail={article.deskEmail}
								lockedByCategory={article?.isLocked}
							/>
						)}
					</Box>
					{!fullWidthContent && verticalDivider}
					<Box
						className={classes.articleInfo}
						style={{
							width: fullWidthContent ? '100%' : '33%',
							marginTop: fullWidthContent ? theme.spacing(4) : 0
						}}
					>
						{!chartsArticle &&
							article.premiumChannelDetails &&
							article.canSeeFullVersion && (
							<Box mb={6}>
								<ChannelCard
									name={
										article.premiumChannelDetails.name
									}
									owner={
										article.premiumChannelDetails.owner
											.name
									}
									url={`${baseUrls.browse.channelMain}/${article.premiumChannelDetails._id}`}
									image={
										article.premiumChannelDetails
											.coverImage
									}
									premium={
										article.premiumChannelDetails
											.type === 'premium'
									}
									isSubscribed={
										article.premiumChannelDetails
											.isCurrentUserSubscribed
									}
								/>
							</Box>
						)}
						{article.deskEmail && (
							<DeskEmail email={article.deskEmail} />
						)}
						<ArticleContributorList
							narrow={chartsArticle}
							publishedBy={article.publishedBy}
							contributors={(
								getUsersWithNames(
									article.contributors as User[]
								) || []
							).map(
								({
									name,
									position,
									_id,
									phone
								}: Partial<User>): ContributorProps => ({
									_id: _id || '',
									name: name || '',
									jobFunction: position || '',
									phone: phone || ''
								})
							)}
						/>
						{!chartsArticle && article?.premiumChannel ? (
							<LatestArticlesFromChannel
								channelId={article?.premiumChannel}
								currentArticleId={article._id}
							/>
						) : (
							<></>
						)}
						{!chartsArticle &&
							article.randomisedPromotionalContent && (
							<PromotionalContentSidebar article={article} />
						)}
					</Box>
				</Box>
			</Grid>
			{!hideDisclaimer ? (
				<Grid item xs={12} className={classes.disclaimer}>
					<Disclaimer />
				</Grid>
			) : (
				<></>
			)}
		</Grid>
	);
};

export default ArticlePage;
